import type { PropsWithChildren } from 'react';
import { useIntl } from 'react-intl';

import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase';
import { IconButton } from '@sorare/core/src/atoms/buttons/IconButton';
import { iconEnvelopeOpenText } from '@sorare/core/src/atoms/icons/system/iconEnvelopeOpenText.ts';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { Badge } from '@sorare/core/src/atoms/ui/Badge';
import { DialogKey } from '@sorare/core/src/components/navigation/WithDialogs';
import { ANY_SPORT_SERIES_INBOX } from '@sorare/core/src/constants/__generated__/routes';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useIsLaptop } from '@sorare/core/src/hooks/device/useIsLaptop';
import { useCloseDialog } from '@sorare/core/src/hooks/navigation/useCloseDialog';
import { useDialogParam } from '@sorare/core/src/hooks/navigation/useDialogParam';
import { useOpenDialog } from '@sorare/core/src/hooks/navigation/useOpenDialog';
import { navLabels } from '@sorare/core/src/lib/glossary';

import { InboxDrawer } from '#src/components/inbox/InboxDrawer/index.tsx';

export const InboxButton = ({ children }: PropsWithChildren) => {
  const { formatMessage } = useIntl();
  const { generateSportContextPath } = useSportContext();
  const { currentUser } = useCurrentUserContext();
  const isLaptopAndAbove = useIsLaptop();
  const openDialog = useOpenDialog();
  const closeDialog = useCloseDialog();
  const open = !!useDialogParam(DialogKey.inbox);

  const unclaimedTasksCount = currentUser?.unclaimedInboxTasksCount || 0;

  return (
    <>
      <ButtonBase
        to={
          isLaptopAndAbove
            ? undefined
            : generateSportContextPath(ANY_SPORT_SERIES_INBOX)
        }
        onClick={
          isLaptopAndAbove
            ? () => {
                openDialog(DialogKey.inbox, 'true');
              }
            : undefined
        }
        aria-label={formatMessage(navLabels.inbox)}
      >
        <Horizontal>
          <Badge badgeContent={unclaimedTasksCount} overlap="circular">
            <IconButton
              size="medium"
              icon={iconEnvelopeOpenText}
              color="transparent"
              asSpan
            />
          </Badge>
          {children}
        </Horizontal>
      </ButtonBase>
      {!!isLaptopAndAbove && (
        <InboxDrawer
          open={open}
          onClose={() => {
            closeDialog(DialogKey.inbox);
          }}
        />
      )}
    </>
  );
};
